/**
 * Generic Schema Migration Dialog + Banner — UI for confirming schema changes.
 *
 * sl-oll9.10: Extracted from elster-migration-dialog.tsx.
 * Shows pending changes and lets users confirm or postpone.
 *
 * SOLID:
 * - S: Only migration UI rendering
 * - O: New diff categories extend the field list, no code changes
 */

import { Button } from '@affine/component/ui/button';
import { useI18n } from '@affine/i18n';
import type { FC } from 'react';

import * as styles from './elster-fullscreen-view.css';
import type { SchemaDiff } from './schema-diff';

// ─── DB Type Labels ─────────────────────────────────────────────────────────

const DB_TYPE_LABELS: Record<string, string> = {
  number: 'Zahl',
  checkbox: 'Checkbox',
  date: 'Datum',
  select: 'Auswahl',
  'rich-text': 'Text',
};

// ─── Migration Dialog ───────────────────────────────────────────────────────

export const SchemaMigrationDialog: FC<{
  diff: SchemaDiff;
  onConfirm: () => void;
  onDismiss: () => void;
  /** Number of blocking rows (0 = safe to update) */
  blockingRows?: number;
  /** Custom warning message when updates are blocked */
  blockingMessage?: string;
}> = ({ diff, onConfirm, onDismiss, blockingRows = 0, blockingMessage }) => {
  const t = useI18n();
  const canUpdate = blockingRows === 0;

  return (
    <div className={styles.migrationOverlay} onClick={onDismiss}>
      <div
        className={styles.migrationDialog}
        onClick={e => e.stopPropagation()}
      >
        <div className={styles.migrationTitle}>
          {t['com.streamlain.integration.erica.schema-update-title']()}
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span className={styles.migrationVersionBadge}>
            {diff.currentVersion}
          </span>
          <span
            style={{
              color: 'var(--streamlain-text-secondary-color)',
              fontSize: '12px',
            }}
          >
            {'\u2192'}
          </span>
          <span className={styles.migrationVersionBadge}>
            {diff.targetVersion}
          </span>
        </div>

        {/* Guard warning when updates are blocked */}
        {!canUpdate && (
          <div
            style={{
              padding: '10px 14px',
              background: 'var(--streamlain-background-warning-color, #FFF3E0)',
              borderRadius: '8px',
              fontSize: '13px',
              color: 'var(--streamlain-text-primary-color)',
              lineHeight: '1.5',
            }}
          >
            {blockingMessage ??
              `Bitte erst alle ${blockingRows} offenen Eintr\u00E4ge absenden oder verwerfen, bevor das Schema aktualisiert wird.`}
          </div>
        )}

        <div className={styles.migrationBody}>
          <div className={styles.migrationFieldList}>
            {diff.added.map(item => (
              <div key={item.fieldId} className={styles.migrationFieldItem}>
                <div className={styles.migrationFieldHeader}>
                  <span className={styles.migrationFieldBadgeAdded}>
                    +{' '}
                    {t['com.streamlain.integration.erica.schema-update-new']()}
                  </span>
                  <span>{item.fieldId}</span>
                </div>
                <div className={styles.migrationFieldLabel}>{item.label}</div>
                <div className={styles.migrationFieldType}>
                  {DB_TYPE_LABELS[item.dbType] ?? item.dbType}
                  {item.enumValues && (
                    <> ({Object.values(item.enumValues).join(', ')})</>
                  )}
                </div>
              </div>
            ))}

            {diff.removed.map(item => (
              <div key={item.fieldId} className={styles.migrationFieldItem}>
                <div className={styles.migrationFieldHeader}>
                  <span className={styles.migrationFieldBadgeRemoved}>
                    - Entfernt
                  </span>
                  <span>{item.fieldId}</span>
                </div>
                <div className={styles.migrationFieldLabel}>
                  {item.columnName}
                </div>
                <div className={styles.migrationFieldType}>
                  Spalte wird gel{'\u00F6'}scht (keine Daten vorhanden)
                </div>
              </div>
            ))}
          </div>

          <div className={styles.migrationInfo}>
            {t['com.streamlain.integration.erica.schema-update-info']()}
          </div>
        </div>

        <div className={styles.migrationActions}>
          <Button variant="secondary" onClick={onDismiss}>
            {t['com.streamlain.integration.erica.schema-update-later']()}
          </Button>
          <Button variant="primary" onClick={onConfirm} disabled={!canUpdate}>
            {canUpdate
              ? t['com.streamlain.integration.erica.schema-update-now']()
              : 'Bitte erst absenden'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Migration Banner ───────────────────────────────────────────────────────

export const SchemaMigrationBanner: FC<{
  diff: SchemaDiff;
  onUpdate: () => void;
}> = ({ diff, onUpdate }) => {
  const t = useI18n();

  return (
    <div className={styles.migrationBanner}>
      <div className={styles.migrationBannerText}>
        <span>
          {t['com.streamlain.integration.erica.schema-update-banner']()}
        </span>
        <span
          style={{
            color: 'var(--streamlain-text-secondary-color)',
            fontSize: '12px',
          }}
        >
          ({diff.added.length}{' '}
          {t['com.streamlain.integration.erica.schema-update-banner-new']()},{' '}
          {diff.removed.length}{' '}
          {t['com.streamlain.integration.erica.schema-update-banner-removed']()}
          )
        </span>
      </div>
      <Button variant="primary" onClick={onUpdate}>
        {t['com.streamlain.integration.erica.schema-update-now']()}
      </Button>
    </div>
  );
};
